/** Capitalizes the first letter of a string. */
export const capitalizeFirst = str => str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Converts a string to title case by replacing specified characters
 * with spaces and capitalizing the first letter of each word.
 *
 * ___
 *
 * @param {string} str - The input string to be converted.
 * @param {string} [toSpace='-_'] - A string containing characters to be replaced with spaces.
 * @returns {string} The converted string in title case or an empty string.
 *
 * @example 'example_string-123' => 'Example String 123'
 */
export const titleCaseReplace = (str, toSpace = '-_') => {
	if (!str) return '';
	const words = str.split(new RegExp(`[${toSpace}]`));
	return words.map(capitalizeFirst).join(' ');
};

/** Checks if a value can be interpreted as a number. */
export const isNum = value => !isNaN(parseFloat(value));

/** Same as `.toFixed()` but does not force trailing zeros */
export const limitDecimal = (num, decimals = 1) => {
	const factor = Math.pow(10, decimals);
	return Math.round(num * factor) / factor;
};

/** Checks if a value is an object and not null. **(Array is considered an object)** */
export const isObj = v => typeof v === 'object' && v !== null;

/**
 * Recursive Deep Compare **val1** and **val2**.
 *
 * > **Note:**
 * > - Functions are compared by reference.
 * > - Does not handle circular references. (Stack overflow)
 * > - Ignores symbol-keyed properties
 * > - Ignores non-enumerable properties.
 *
 * @param {*} val1 - Value 1
 * @param {*} val2 - Value 2
 * @returns {boolean} True if values are equal, else false
 */
export const deepCompare = (val1, val2) => {
	// Instantly check non-objects and nulls for equality. (also handles NaN)
	if (!isObj(val1) || !isObj(val2)) return Object.is(val1, val2);

	const keys1 = Object.keys(val1);
	const keys2 = Object.keys(val2);

	// Ensure keys have identical length
	if (keys1.length !== keys2.length) return false;

	// Recursively compare values for each key.
	for (const key of keys1) {
		if (!Object.hasOwn(val2, key)) return false;

		const v1 = val1[key];
		const v2 = val2[key];
		if (isObj(v1) && isObj(v2)) {
			if (!deepCompare(v1, v2)) return false;
		} else if (!Object.is(v1, v2)) return false;
	}

	return true;
};

/**
 * Formats a number with commas as thousands separators and rounds to specified decimal places.
 * If the input cannot be parsed as a number, the original input is returned.
 *
 * ___
 *
 * @param {number|string} number - The number to format
 * @param {number} [decimalPlaces=1] - The number of decimal places to round to
 * @returns {string} The formatted number with thousands separators
 *
 * @example
 * // Format integer with default decimal places
 * formatNumber(1000); // Returns "1,000.00"
 *
 * @example
 * // Format decimal with custom decimal places
 * formatNumber(1234.5678, 3); // Returns "1,234.568"
 */
export const formatNumber = (value, decimalPlaces = 1) => {
	const roundedNumber = limitDecimal(value, decimalPlaces);
	if (!isNum(roundedNumber)) return value;

	// Use toLocaleString() to add commas as thousands separators
	return roundedNumber.toLocaleString();
};

// TODO: The above format function should probably be replaced with Intl.NumberFormat? (Not sure if it is needed or better)
/* Example (Not checked):
//
//	export const formatNumber = (number, decimalPlaces = 2) => {
//		if (isNaN(parseFloat(number))) return number;
//		
//		return new Intl.NumberFormat(undefined, {
//			minimumFractionDigits: decimalPlaces,
/				maximumFractionDigits: decimalPlaces
/			}).format(number);
//	};
*/

/** day -> daily | week -> weekly | month -> monthly | year -> yearly */
export const recurringIntervalSwitch = interval => {
	switch (interval) {
		case 'day':
			return 'daily';
		case 'week':
			return 'weekly';
		case 'month':
			return 'monthly';
		case 'year':
			return 'yearly';
		default:
			return null;
	}
};

/**
 * Returns a new array containing unique items based on the callback function.
 *
 * ___
 *
 * @param {Array} array The array to filter.
 * @param {Function} callback The callback function used to determine uniqueness. (Default: item => item)
 * @returns {Array} A new array with unique items.
 *
 * @example
 * const numbers = [1, 2, 3, 4, 5, 5, 4, 3, 2, 1];
 * const uniqueNumbers = uniqueBy(numbers);
 * console.log(uniqueNumbers); // Output: [1, 2, 3, 4, 5]
 *
 * const users = [
 *   { id: 1, name: 'John' },
 *   { id: 2, name: 'Jane' },
 *   { id: 3, name: 'John' },
 *   { id: 4, name: 'Jane' }
 * ];
 * const uniqueUsers = uniqueBy(users, user => user.name);
 * console.log(uniqueUsers); // Output: [{ id: 1, name: 'John' }, { id: 2, name: 'Jane' }]
 */
export const uniqueBy = (array, callback = item => item) => {
	const accumulator = new Set();
	return array.filter(item => {
		const value = callback(item);
		if (accumulator.has(value)) return false;
		accumulator.add(value);
		return true;
	});
};

/**
 * Returns an array of unique values from an array based on a callback function.
 *
 * ___
 *
 * @param {Array} array The input array.
 * @param {Function} callback The callback function used to determine uniqueness. (Default: item => item)
 * @returns {Array} An array of unique values from the input array.
 *
 * @example
 * const users = [
 *   { id: 1, name: 'John' },
 *   { id: 2, name: 'Jane' },
 *   { id: 3, name: 'John' },
 *   { id: 4, name: 'Jane' }
 * ];
 * const uniqueNames = uniqueValuesBy(users, user => user.name);
 * console.log(uniqueNames); // Output: ['John', 'Jane']
 */
export const uniqueValuesBy = (array, callback = item => item) =>
	Array.from(new Set(array.map(item => callback(item))));

/**
 * Formats a date string or Date object to Norwegian locale format
 *
 * ___
 *
 * @param {string|Date} d - The date to format (can be a date string or Date object)
 * @returns {string} The formatted date string in Norwegian format (e.g., "15. jan. 2024")
 */
export const formatDate = d =>
	new Date(d).toLocaleDateString('nb-NO', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	});

/** Returns true or false based on if Cookiebot is initialized correctly */
export const isCookiebotInitialized = () => {
	try {
		if (Cookiebot instanceof HTMLElement) return false;
		else return !!Cookiebot;
	} catch (error) {
		return false;
	}
};

/**
 * Retrieves the cookie consent status from Cookiebot.
 *
 * - If Cookiebot is defined, it returns the consent object.
 * - If Cookiebot is not defined, it returns a default consent state with necessary cookies enabled and others disabled.
 * ___
 *
 * @returns {Object} An object representing the cookie consent status.
 */
export const getCookieConsent = () => {
	try {
		if (Cookiebot instanceof HTMLElement)
			throw new Error(
				'Cookiebot failed to load. (This may be due to using strict mode or adblockers)'
			);
		return Cookiebot.consent;
	} catch (error) {
		console.error(error);

		// If Cookiebot is not defined, return a default consent state
		return {
			necessary: true,
			preferences: false,
			statistics: false,
			marketing: false,
		};
	}
};

/**
 * Opens the Cookiebot cookie consent dialog.
 *
 * ___
 *
 * @param {Event} e - The event object that triggered the function
 * @returns {void}
 *
 * @example
 * // Usage in event handler
 * element.addEventListener('click', openCookieConsent);
 */
export const openCookieConsent = e => {
	e.preventDefault();

	try {
		Cookiebot.renew();
	} catch (error) {
		console.group('Error opening cookie consent dialog');
		console.warn(error);
		console.groupEnd();
	}
};
